import { useState } from 'react';

import { FundOutlined } from '@ant-design/icons/lib/icons';
import { Col, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { doCancelOfferOrder, doClearCancelOfferOrder, getOfferDetails } from 'src/actions';
import { DateConstant, FilterBarConstant } from 'src/constants';
import { OfferOrderPaymentTypeDto, OfferOrderStatusDto } from 'src/dtos';
import { useOfferOrders, useSubmittedAccountSelector } from 'src/hooks';
import {
  CancelOfferOrderButton,
  ConfirmActionModal,
  CurrencyField,
  CurrencyRangeField,
  ExternalOfferDisclaimerModal,
  FilterBar,
  MAlert,
  MCard,
  MTable,
  MTypography,
  OfferOrderStatusTag,
  SecuritiesHeldAndFundedOutsideDisclaimerModal,
} from 'src/lib';
import { OfferDetails, OfferOrder, OfferOrderStatusLabel } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';
import { getOfferOrderStatusTagColor, mapFilterToGetOfferOrderListQueryParamsDto, Maybe } from 'src/utils';

const defaultValues: FilterBarConstant.Sample = {
  sortBy: FilterBarConstant.SortBy.Newest,
  timeline: FilterBarConstant.FixedTimeline.ThisYear,
  customTimeline: undefined,
  filterBy: [],
  show: [],
};

export const OfferOrdersTable = () => {
  const isCancelOrderLoading = useSelector((state: any) => state.offerOrders.cancel.isLoading);
  const cancelOrderError = useSelector((state: any) => state.offerOrders.cancel.error);
  const cancelOrderFailed = useSelector((state: any) => Boolean(state.offerOrders.cancel.__failed));
  const cancelOrderSuccess = useSelector((state: any) => Boolean(state.offerOrders.cancel.__succeeded));

  const offerDetails: Maybe<OfferDetails> = useSelector((state: any) => state.offers.offerDetails?.data);
  const isOfferDetailsLoading: boolean = useSelector((state: any) => state.offers.offerDetails?.isLoading);

  const [selectedCancelOrder, setSelectedCancelOrder] = useState<OfferOrder | null>(null);
  const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState(false);

  const [isSecuritiesPurchasedByCardModalOpen, setIsSecuritiesPurchasedByCardModalOpen] = useState<boolean>(false);
  const [isExternalOfferDisclaimerOpen, setIsExternalOfferDisclaimerOpen] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<FilterBarConstant.Sample>(defaultValues);

  const { account } = useSubmittedAccountSelector();
  const { offerOrderList, isOfferOrderListLoading, offerOrderListTotal } = useOfferOrders({
    queryParams: mapFilterToGetOfferOrderListQueryParamsDto(filters, { take: 10, skip: (currentPage - 1) * 10 }),
    dependency: [filters.timeline, filters.customTimeline, filters.filterBy, currentPage, account.accountId],
  });
  const dispatch = useDispatch();

  const onCancel = (order: OfferOrder) => {
    setSelectedCancelOrder(order);
    setIsCancelOrderModalOpen(true);
  };

  const onConfirmCancelOrder = (selectedItem: OfferOrder) => {
    dispatch(doCancelOfferOrder({ params: { id: selectedItem.id, accountId: account.accountId } }));
  };

  const onViewPaymentCardDisclaimer = () => {
    setIsSecuritiesPurchasedByCardModalOpen(true);
  };

  const onViewExternalDisclaimer = (offerOrder: OfferOrder) => {
    setIsExternalOfferDisclaimerOpen(true);

    if (offerDetails?.id !== offerOrder.offerId) {
      dispatch(getOfferDetails(offerOrder.offerId));
    }
  };

  const mapOfferOrderStatusToStatusFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options: FilterBarConstant.GroupOptionItem[] = Object.values(OfferOrderStatusLabel).map(value => ({
      label: <OfferOrderStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.OfferOrder}`,
      tag: {
        text: value,
        ...getOfferOrderStatusTagColor(value),
      },
    }));

    return [{ options }];
  };

  const onApproveFilters = (newFilters: FilterBarConstant.Sample) => {
    setFilters({ ...newFilters });
  };

  const cols: ColumnsType<OfferOrder> = [
    {
      title: 'Offer Name',
      width: '30%',
      render: (value: OfferOrder) => {
        return (
          <Row>
            <Col span={24}>
              <MTypography variant='body1'>{value.offerName}</MTypography>
            </Col>
            {value.paymentType.isCard ? (
              <Col span={24}>
                <MAlert
                  type='warning'
                  icon={<FundOutlined />}
                  description={
                    <>
                      <MTypography variant='body1' inline>
                        Securities purchased by credit card are funded and held outside of your My IPO account.
                      </MTypography>
                      <MTypography variant='body1' inline color={ColorConstant.ORANGE8}>
                        {' '}
                        [View More]
                      </MTypography>
                    </>
                  }
                  onClick={onViewPaymentCardDisclaimer}
                  testId='history-order-creditcard-disclaimer'
                />
              </Col>
            ) : null}
            {value.isExternal ? (
              <Col span={24}>
                <MAlert
                  type='warning'
                  description={
                    <>
                      <MTypography variant='body1' inline>
                        This type of offering is held & funded outside of My IPO.
                      </MTypography>
                      <MTypography variant='body1' inline color={ColorConstant.ORANGE8}>
                        {' '}
                        [View More]
                      </MTypography>
                    </>
                  }
                  onClick={() => onViewExternalDisclaimer(value)}
                  testId='history-order-external-disclaimer'
                />
              </Col>
            ) : null}
          </Row>
        );
      },
    },
    {
      title: 'Date',
      render: (value: OfferOrder) => moment(value.createdAt).format(DateConstant.US_FORMAT),
    },
    {
      title: 'Quantity',
      render: (value: OfferOrder) => {
        if (value.conditional) {
          return ![OfferOrderStatusDto.Approved, OfferOrderStatusDto.Complete].includes(value.status.value)
            ? 'TBD'
            : value.quantity;
        }

        return value.quantity;
      },
    },
    {
      title: 'Order Price',
      render: (value: OfferOrder) => {
        if (value.conditional) {
          return ![OfferOrderStatusDto.Approved, OfferOrderStatusDto.Complete].includes(value.status.value) ? (
            <CurrencyRangeField min={value.conditional.minSharePrice} max={value.conditional.maxSharePrice} />
          ) : (
            <CurrencyField value={value.purchasePrice} />
          );
        }

        return <CurrencyField value={value.purchasePrice} />;
      },
    },
    {
      title: 'Amount',
      render: (value: OfferOrder) => {
        if (value.conditional) {
          return ![OfferOrderStatusDto.Approved, OfferOrderStatusDto.Complete].includes(value.status.value) ? (
            <MTypography variant='h6' color={ColorConstant.RED8} weight='bold'>
              <CurrencyField value={-value.conditional.totalInvestment} />
            </MTypography>
          ) : (
            <MTypography variant='h6' color={ColorConstant.RED8} weight='bold'>
              <CurrencyField value={-value.value} />
            </MTypography>
          );
        }

        return (
          <MTypography variant='h6' color={ColorConstant.RED8} weight='bold'>
            <CurrencyField value={-value.value} />
          </MTypography>
        );
      },
    },
    {
      title: 'Status',
      render: (value: OfferOrder) => <OfferOrderStatusTag value={value.status.label} />,
      width: 250,
    },
    {
      title: 'Action',
      key: 'action',
      render: (value: OfferOrder) => <CancelOfferOrderButton value={value} onClick={onCancel} />,
      width: 100,
    },
  ];

  return (
    <Row>
      <Col span={24} id='orders-section'>
        <Row>
          <Col span={24} className={Spacing.my16}>
            <MTypography variant='h4' weight='semibold' color={ColorConstant.BRAND6}>
              Offer Orders
            </MTypography>
          </Col>
          <Col span={24}>
            <FilterBar
              sortByOptions={[{ label: FilterBarConstant.SortByLabel.Newest, value: FilterBarConstant.SortBy.Newest }]}
              byFilter={mapOfferOrderStatusToStatusFilterGroup()}
              onApprove={onApproveFilters}
              defaultValue={defaultValues}
              initValue={filters}
            />
          </Col>
          <Col span={24} className={Spacing.my16}>
            <MCard>
              <MTable<OfferOrder>
                columns={cols}
                data={offerOrderList}
                pagination={{
                  position: ['bottomRight'],
                  showSizeChanger: false,
                  current: currentPage,
                  pageSize: 10,
                  total: offerOrderListTotal,
                }}
                loading={isOfferOrderListLoading}
                scroll={{ y: 750 }}
                rowKey={record => record.id}
                onChange={pagination => {
                  setCurrentPage(pagination.current ?? 1);
                }}
                testId={'offer-orders-table'}
              />
            </MCard>
          </Col>
        </Row>
      </Col>
      {selectedCancelOrder && (
        <ConfirmActionModal
          title='Cancel Confirmation'
          message='Are you sure you want to cancel this offer order?'
          successMessage='Offer order cancellation request was successfully submitted.'
          errorMessage={cancelOrderError?.message}
          isOpen={isCancelOrderModalOpen}
          isError={cancelOrderFailed}
          isSuccess={cancelOrderSuccess}
          isLoading={isCancelOrderLoading}
          onCancel={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
          }}
          onOk={() => {
            onConfirmCancelOrder(selectedCancelOrder);
          }}
          onClose={() => {
            setIsCancelOrderModalOpen(false);
            setSelectedCancelOrder(null);
            dispatch(doClearCancelOfferOrder());
          }}
        />
      )}
      <SecuritiesHeldAndFundedOutsideDisclaimerModal
        type={OfferOrderPaymentTypeDto.Card}
        isVisible={isSecuritiesPurchasedByCardModalOpen}
        onClose={() => setIsSecuritiesPurchasedByCardModalOpen(false)}
        onOK={() => setIsSecuritiesPurchasedByCardModalOpen(false)}
      />
      <ExternalOfferDisclaimerModal
        isOpen={isExternalOfferDisclaimerOpen}
        isLoading={isOfferDetailsLoading}
        disclaimer={offerDetails?.externalDetails?.disclaimer}
        onClose={() => {
          setIsExternalOfferDisclaimerOpen(false);
        }}
      />
    </Row>
  );
};
